/*
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
*/

import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { Router, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG } from '@azure/msal-angular';
import { InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthService } from './shared/services/auth/auth.service';
import { RedirectService } from './shared/services/auth/redirect.service';
import { ScrollService } from './shared/utils/scroll.service';
import { VersionService } from './shared/utils/version.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'ADC Portal';
  isIframe = false;
  loginDisplay = false;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: any,
    private broadcastService: MsalBroadcastService,
    private msalService: MsalService,
    private authService: AuthService,
    private redirectService: RedirectService,
    private scrollService: ScrollService,
    private versionService: VersionService,
    private router : Router,
    private location: Location
  ) {
    console.log('App version:', this.versionService.getCurrentVersion());
  }

  ngOnInit(): void {
    this.isIframe = window !== window.parent && !window.opener;

    this.redirectService.handleRedirectPromise().then(() => {
      this.setLoginDisplay();
      this.authService.refreshAuthStatus();
    });

    this.broadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.authService.refreshAuthStatus();
      });

      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          (window as any).clarity?.("set", "route", event.urlAfterRedirects);
        }
      });

      this.location.onUrlChange(url => {
        const fullUrl = new URL(window.location.href);
        const params = new URLSearchParams(fullUrl.search);
        const athenaSamples = params.get('sample');

        if (athenaSamples) {
          (window as any).clarity?.("set", "sample", athenaSamples);
        }
      });
  }

  setLoginDisplay() {
    this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0;
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
